import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { LanguageDropdown } from './LanguageDropdown';

const PAGE_LINKS = [
  { labelKey: 'footer.pages.products', to: '/products' },
  { labelKey: 'footer.pages.technology', to: '/technology' },
  { labelKey: 'footer.pages.ourStory', to: '/our-story' },
  { labelKey: 'footer.pages.patientCare', to: '/patient-care' },
  { labelKey: 'footer.pages.contact', to: '/contact' },
] as const;

/**
 * Footer.
 *
 * Mobile  : 3 stacked sections — Language, Pages, Reserved (logo on top of copyright).
 * Tablet+ : Language and Pages collapse into a 2-column "Information" bar (vertical
 *           border-l between them); Reserved bar below with copyright on the left and
 *           the wordmark on the right.
 *
 * Pages list re-flows: mobile = vertical, tablet = 2×2 grid, small/large/xl = single
 * horizontal row with gap-20.
 */
export function Footer() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const language = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const setLanguage = (next: string) => {
    void i18n.changeLanguage(next);
  };
  return (
    <footer className="bg-clearlab-divider-light/20 text-clearlab-midnight">
      <div className="flex flex-col md:flex-row md:items-stretch">
        <section
          aria-labelledby="footer-language-heading"
          className="flex flex-col gap-4 p-6 md:flex-1 md:gap-8 md:p-14 lg:flex-1 lg:px-20 lg:py-14 xl:w-[485px] xl:flex-none xl:px-30 xl:py-14 2xl:w-auto 2xl:flex-none 2xl:py-14 2xl:pl-60 2xl:pr-30"
        >
          <h2
            id="footer-language-heading"
            className="text-label-14 font-semibold text-clearlab-midnight"
          >
            {t('footer.languageHeading')}
          </h2>
          <LanguageDropdown
            value={language}
            onChange={setLanguage}
            className="w-full min-w-[140px] 2xl:w-[245px]"
            menuPlacement="top"
            triggerClassName="p-4"
          />
        </section>

        <section
          aria-labelledby="footer-pages-heading"
          className="flex flex-col gap-4 border-t border-clearlab-divider-darker/60 p-6 md:flex-1 md:gap-8 md:border-l md:border-t-0 md:p-14 lg:px-20 lg:py-14 min-[1180px]:flex-none xl:flex-1 xl:px-30 xl:py-14 2xl:flex-1 2xl:px-30"
        >
          <h2
            id="footer-pages-heading"
            className="text-label-14 font-semibold text-clearlab-midnight"
          >
            {t('footer.pagesHeading')}
          </h2>
          <ul className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-x-10 md:gap-y-4 min-[1180px]:flex min-[1180px]:flex-row min-[1180px]:flex-nowrap min-[1180px]:gap-x-20">
            {PAGE_LINKS.map((link) => (
              <li key={link.to} className="min-[1180px]:whitespace-nowrap">
                <Button to={link.to} variant="transparent" size="small" tone="black">
                  {t(link.labelKey)}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="flex flex-col gap-4 border-t border-clearlab-divider-darker/60 p-6 md:flex-row md:items-center md:gap-10 md:px-14 md:py-6 lg:px-20 xl:px-30 2xl:px-60">
        <Link to="/" aria-label={t('nav.brand.homeLabel')} className="inline-flex md:order-2">
          <img src="/Logo_Mono.svg" alt="Clearlab" width={189} height={40} className="h-4 w-auto" />
        </Link>
        <p className="text-body-14 leading-5 text-clearlab-steel md:order-1 md:flex-1 lg:whitespace-nowrap">
          <Trans
            i18nKey="footer.copyright"
            values={{ year }}
            components={{
              privacy: (
                <Link
                  to="/legal/privacy"
                  className="underline transition-colors hover:text-clearlab-blue"
                />
              ),
              dataProtection: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content -- Trans injects child text content at runtime.
                <a
                  href="/legal/DATA-PROTECTION-POLICY-DATED-23-MAR-2021.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors hover:text-clearlab-blue"
                />
              ),
            }}
          />
        </p>
      </div>
    </footer>
  );
}
